import $ from 'jquery';
import _ from 'underscore';
import Raven from 'raven-js';

function supportsLocalStorage() {
  try {
    return 'localStorage' in window && window.localStorage !== null;
  } catch (e) {
    // e can be SecurityError: DOM EXception 18
    return false;
  }
}

/** @param {string} key */
function deleteItem(key) {
  localStorage.removeItem(key);
}

/**
 * @param {string} key
 * @param {int} timeout
 */
function checkExpiration(savedObj, key, timeout) {
  const isExpiredKey = savedObj.timestamp ? savedObj.timestamp <= Date.now() - timeout : false;
  if (isExpiredKey) {
    deleteItem(key);
  }
}

/**
 * @param {JQuery} $field
 */
export function setVal($field, savedObj = null) {
  if (!$field.length) {
    return;
  }

  _.defer(() => {
    const $input = $field.find('textarea:not(.clone), input[type="text"]');
    const data = $field.data();
    const field = data.autosaveField;
    // passive means that the field's value won't be updated by this function
    // but will still save when it is changed
    const passive = data.autosavePassive === true;

    // savedObj was not passed in
    // need to parse savedObj from localStorage[key]
    if (!savedObj) {
      let key = data.autosaveKey;
      const user = data.autosaveUser;
      key = user ? `${user}_${key}` : key;
      // eslint-disable-next-line no-param-reassign
      savedObj = localStorage[key] ? JSON.parse(localStorage[key]) : {};
    }

    if (!passive) {
      const savedText = savedObj[field];
      if (savedText && $input.val().trim() === '') {
        $input.val(savedText);
        $input.trigger($.Event('input'));
      }
    }
  });
}

/**
 * @param {string} key
 * @param {string} value
 */
function save(key, value) {
  try {
    localStorage[key] = value;
  } catch (e) {
    // do nothing
  }
}

/**
 * autosave in localstorage
 * @param {JQuery} $field
 *
 * TO USE:
 * add to $field
 *      data-controller="util/autosave:controller"
 *      data-autosave-key="blah"
 *      data-autosave-hours="blah"
 *      data-autosave-field="blah"
 *      data-autosave-user="blah"
 *      data-autosave-passive="blah"    // optional
 *      data-autosave-hash="blah"       // optional
 * submit button needs class="autosave-submit"
 */
export function controller($field) {
  if (!supportsLocalStorage()) {
    return;
  }

  const $form = $field.parents('form');
  const $input = $field.find('textarea, input[type="text"]');
  const $submit = $form.find('.autosave-submit');
  const data = $field.data();
  const hours = data.autosaveHours;
  const field = data.autosaveField;
  const user = data.autosaveUser;
  const hash = data.autosaveHash;
  let key = data.autosaveKey;
  let savedObj = {};
  let initValue;

  key = user ? `${user}_${key}` : key;
  if (localStorage[key]) {
    savedObj = JSON.parse(localStorage[key]);
  }

  // check if has been in localstorage for more than x hours
  const timeout = hours * 60 * 60 * 1000; // in ms
  checkExpiration(savedObj, key, timeout);

  // if in localstorage and not passive then set input value
  setVal($field, savedObj);

  const localSave = el => {
    try {
      const value = $(el).val();
      if (savedObj[field] !== value) {
        savedObj[field] = value;
        savedObj.timestamp = Date.now();
        if (hash && !savedObj.hash) {
          savedObj.hash = hash;
        }

        const savedStr = JSON.stringify(savedObj);
        save(key, savedStr);

        if (value !== initValue) {
          // alert user that there are unsaved edits
          // if they try to navigate away from page
          window.onbeforeunload = () => 'You have unsaved edits.';
        } else {
          window.onbeforeunload = null;
        }
      }
    } catch (e) {
      Raven.captureException(e);
    }
  };

  $input.one('focus', () => {
    initValue = $input.val();
    if (localStorage[key]) {
      savedObj = JSON.parse(localStorage[key]);
    }
  });

  // save to localstorage
  $input.on('input', e => localSave(e.currentTarget));

  // for manually triggering saves
  $input.on('autosave.save', e => localSave(e.currentTarget));

  // remove from localstorage
  const clear = () => {
    deleteItem(key);

    // no more unsaved edits
    // no need to alert users
    window.onbeforeunload = null;
  };

  $submit.on('click', clear);
  $form.on('autosave.clear', clear);
}

/**
 * remove edits from localstorage
 * @param {JQuery} key
 */
export function discardEdit(key) {
  // remove from localstorage
  deleteItem(key);
  // suppress unsaved edits alert
  window.onbeforeunload = null;
}

/**
 * return edit hash
 * @param {JQuery} key
 */
export function getEditHash(key) {
  if (localStorage[key]) {
    const savedObj = JSON.parse(localStorage[key]);
    return savedObj.hash;
  }
  return null;
}
